/**
 * Pinned boards (Boards Redesign extras, EXTRAS.md §2.2): per user, synced
 * across the user's own devices, private — like Trello stars.
 *
 * Nothing device-local fits (appPreferencesStore / boardsLocalPrefs never
 * sync), the backend `settings` table is admin configuration and
 * `/chat/preferences` is chat only. The per-user synced channel that DOES
 * exist is the personal scope, so each pin is one personal-scope record:
 *
 *   extension `task-manager`, record_type `board_pin`, id `pin-<boardId>`,
 *   data `{ boardId, pinnedAt }`. Unpinning tombstones it.
 *
 * One record per pin, not one settings record, so pinning two boards on two
 * devices at the same time cannot lose a pin to last-write-wins. Pins always
 * go to `personalScopeId()` — also for team and device-only boards — so
 * teammates never see them; signed out that is `personal:local`, which moves
 * into the account on sign-in like every other personal record.
 *
 * The record deliberately has NO `scope_id`. A board's own records are found
 * by `scope_id = <boardId>` and the app's whole-board writer tombstones every
 * record with that scope_id it was not handed — a pin carrying the board's id
 * there would be deleted by the board's next save.
 *
 * Every board reader filters on `record_type: 'board'` or a board's scope id,
 * and the Free-plan quota counts only `board` rows, so a pin costs bytes and
 * nothing else. No backend change. A pin whose board is gone is ignored
 * (`pinnedIdsIn`) and removed when the board is deleted from the app.
 */
import { listRecords, upsertRecord, deleteRecord } from '../../../records/recordsClient';
import { personalScopeId } from '../../../services/personalScope';
import { useBoardsUiStore } from './boardsUiStore';

const EXTENSION_ID = 'task-manager';
export const PIN_RECORD_TYPE = 'board_pin';

/** The record id of a board's pin. */
export const pinRecordId = (boardId) => `pin-${boardId}`;

/** One pin record, as `upsertRecord` takes it. */
export function pinRecordFor(boardId, pinnedAt = new Date().toISOString()) {
  return {
    id: pinRecordId(boardId),
    record_type: PIN_RECORD_TYPE,
    parent_id: null,
    sort_key: null,
    data: { boardId: String(boardId), pinnedAt },
  };
}

/**
 * Listed pin rows (flat `record_json` shape) → `{ [boardId]: pinnedAt ms }`.
 * Rows without a board id, or deleted ones, are skipped; an unreadable
 * `pinnedAt` still pins (at 0) rather than silently unpinning.
 */
export function pinMapFromRows(rows) {
  const out = {};
  for (const r of rows || []) {
    if (!r || r.deleted || r.record_type !== PIN_RECORD_TYPE) continue;
    const boardId = r.boardId != null ? String(r.boardId) : r.data?.boardId != null ? String(r.data.boardId) : '';
    if (!boardId) continue;
    const raw = r.pinnedAt ?? r.data?.pinnedAt;
    const at = typeof raw === 'number' ? raw : Date.parse(raw || '');
    out[boardId] = Math.max(out[boardId] || 0, Number.isFinite(at) ? at : 0);
  }
  return out;
}

/** Is this board pinned in `pinned` (the store's map)? */
export function isBoardPinned(pinned, boardId) {
  return boardId != null && Object.prototype.hasOwnProperty.call(pinned || {}, String(boardId));
}

/** The pinned ids among `boards` — orphan pins (board gone) are ignored. */
export function pinnedIdsIn(boards, pinned) {
  return (boards || []).map((b) => String(b?.id ?? '')).filter((id) => id && isBoardPinned(pinned, id));
}

const ctx = () => ({ workspaceId: personalScopeId(), extensionId: EXTENSION_ID });

/** Read every pin of the signed-in user into `useBoardsUiStore.pinned`. */
export async function loadBoardPins() {
  try {
    const rows = await listRecords({ ...ctx(), recordType: PIN_RECORD_TYPE, scopeId: null });
    const pinned = pinMapFromRows((rows || []).map((r) => ({ record_type: PIN_RECORD_TYPE, ...r })));
    useBoardsUiStore.getState().setPinned(pinned);
    return pinned;
  } catch (err) {
    console.warn('[task-manager] reading board pins failed:', err);
    return useBoardsUiStore.getState().pinned;
  }
}

/**
 * Pin a board. The store updates first (the star fills at once) and rolls
 * back if the write fails.
 */
export async function pinBoard(boardId, at = Date.now()) {
  const id = String(boardId);
  const store = useBoardsUiStore.getState();
  const before = store.pinned;
  store.setPinned({ ...before, [id]: at });
  try {
    await upsertRecord({ ...ctx(), record: pinRecordFor(id, new Date(at).toISOString()) });
  } catch (err) {
    useBoardsUiStore.getState().setPinned(before);
    throw err;
  }
}

/** Unpin a board (also the clean-up when a board is deleted). */
export async function unpinBoard(boardId) {
  const id = String(boardId);
  const store = useBoardsUiStore.getState();
  const before = store.pinned;
  if (isBoardPinned(before, id)) {
    const next = { ...before };
    delete next[id];
    store.setPinned(next);
  }
  try {
    await deleteRecord({ ...ctx(), recordId: pinRecordId(id) });
  } catch (err) {
    if (isBoardPinned(before, id)) useBoardsUiStore.getState().setPinned(before);
    throw err;
  }
}

/** Pin or unpin, whichever it is not. Resolves to the new state. */
export async function toggleBoardPin(boardId) {
  if (isBoardPinned(useBoardsUiStore.getState().pinned, boardId)) {
    await unpinBoard(boardId);
    return false;
  }
  await pinBoard(boardId);
  return true;
}
